import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, ErrorBox, Options } from "./common";
import ProjectForm from "./ProjectForm";
import { errorMessage, fmtDateTime, fmtMoney, fmtNum } from "../dmApi";
import { FaMapMarkerAlt, FaPlus, FaTimes, FaUser } from "react-icons/fa";
import { ResultsMobileCard, ResultsMobileList } from "../../../components/ResultsMobile";
import PortalFiltersCollapsible, { usePortalCompactFilters } from "../../../portal/PortalFiltersCollapsible";

function stageTone(stage) {
  const v = String(stage || "").toLowerCase();
  if (/(cancel|lost|closed|stopped)/.test(v)) return "danger";
  if (/(pause|hold|wait|review)/.test(v)) return "warning";
  if (/(live|active|running|complete|done)/.test(v)) return "success";
  return "neutral";
}

/** DM projects of one tenant (staff pass tenantId; portal calls are already tenant-scoped). */
function ProjectList({
  api,
  meta,
  basePath,
  tenantId,
  tenantName,
  canCreate = true,
  title = "Digital Marketing projects",
}) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ search: "", stage: "", priority: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ""));
      if (api.isStaff) params.tenant_id = tenantId;
      setProjects((await api.listProjects(params)) || []);
    } catch (err) {
      setError(errorMessage(err, "Could not load projects"));
    } finally {
      setLoading(false);
    }
  }, [api, filters, tenantId]);

  useEffect(() => {
    const timer = setTimeout(load, filters.search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, filters.search]);

  const setFilter = (key) => (e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }));
  const { filtersOpen, setFiltersOpen, isCompactViewport } = usePortalCompactFilters();
  const portalLayout = !api.isStaff;
  const hasActiveFilters = Boolean(filters.search || filters.stage || filters.priority);

  const newProjectButton = canCreate ? (
    <button
      type="button"
      className={`dmp-btn dmp-btn--new${creating ? " is-open" : ""}`}
      onClick={() => setCreating((v) => !v)}
    >
      {creating ? <FaTimes aria-hidden="true" /> : <FaPlus aria-hidden="true" />}
      <span>{creating ? "Close" : "New project"}</span>
    </button>
  ) : null;

  const stagePriorityFields = (
    <>
      <label className="dmp-field">
        Stage
        <select value={filters.stage} onChange={setFilter("stage")}>
          <Options values={meta?.stages} blank="All stages" />
        </select>
      </label>
      <label className="dmp-field">
        Priority
        <select value={filters.priority} onChange={setFilter("priority")}>
          <Options values={meta?.priorities} blank="All" />
        </select>
      </label>
    </>
  );

  return (
    <>
      {portalLayout ? (
        <PortalFiltersCollapsible
          search={filters.search}
          onSearchChange={(value) => setFilters((prev) => ({ ...prev, search: value }))}
          searchPlaceholder={isCompactViewport ? "Search" : "Search project, code or location"}
          searchAriaLabel="Search projects"
          filtersOpen={filtersOpen}
          setFiltersOpen={setFiltersOpen}
          isCompactViewport={isCompactViewport}
          hasActiveFilters={hasActiveFilters}
          onClear={() => setFilters({ search: "", stage: "", priority: "" })}
          actions={
            canCreate ? (
              <button
                type="button"
                className="cportal-btn cportal-btn-primary"
                onClick={() => navigate(`${basePath}/new`)}
              >
                New project
              </button>
            ) : null
          }
        >
          <div className="cportal-filter-row">
            <select value={filters.stage} onChange={setFilter("stage")} aria-label="Stage">
              <Options values={meta?.stages} blank="All stages" />
            </select>
            <select value={filters.priority} onChange={setFilter("priority")} aria-label="Priority">
              <Options values={meta?.priorities} blank="All priorities" />
            </select>
          </div>
        </PortalFiltersCollapsible>
      ) : null}

      {creating ? (
        <div className="dmp-card">
          <h3 className="dmp-card__title">New project{tenantName ? ` for ${tenantName}` : ""}</h3>
          <ProjectForm
            api={api}
            meta={meta}
            tenantId={tenantId}
            onCancel={() => setCreating(false)}
            onSaved={(saved) => {
              setCreating(false);
              navigate(`${basePath}/${saved.Dm_Project_Id}`);
            }}
          />
        </div>
      ) : null}

      <div className="dmp-card dmp-card--flat-compact dmp-project-list">
        <h3 className="dmp-card__title">
          {title}
          {portalLayout ? null : newProjectButton}
        </h3>
        {portalLayout ? null : (
          <div className="dmp-filters">
            <label className="dmp-field dmp-project-list__search" style={{ flex: 1, minWidth: 200 }}>
              Search
              <input value={filters.search} onChange={setFilter("search")} placeholder="Project, code or location" />
            </label>
            {stagePriorityFields}
          </div>
        )}

        <ErrorBox message={error} />

        <ResultsMobileList
          loading={loading}
          emptyLabel={canCreate ? "No projects yet. Use “New project” to start." : "No projects yet."}
        >
          {projects.map((p) => {
            const open = () => navigate(`${basePath}/${p.Dm_Project_Id}`);
            const inReview = p.Counts?.pending_review;
            return (
              <ResultsMobileCard
                key={p.Dm_Project_Id}
                title={p.Project_Name}
                meta={[
                  p.Stage ? { label: p.Stage, tone: stageTone(p.Stage) } : null,
                  p.Short_Code ? { label: p.Short_Code, tone: "info" } : null,
                  p.Priority ? { label: p.Priority, tone: "info" } : null,
                ].filter(Boolean)}
                onOpen={open}
              >
                {p.Location || p.Assigned_To_Name ? (
                  <ul className="co-tcard__contact">
                    {p.Location ? (
                      <li>
                        <FaMapMarkerAlt aria-hidden="true" />
                        <span>{p.Location}</span>
                      </li>
                    ) : null}
                    {p.Assigned_To_Name ? (
                      <li>
                        <FaUser aria-hidden="true" />
                        <span>{p.Assigned_To_Name}</span>
                      </li>
                    ) : null}
                  </ul>
                ) : null}
                <dl className="co-tcard__info co-tcard__info--3">
                  <div>
                    <dt>Content</dt>
                    <dd>
                      {fmtNum(p.Counts?.content)}
                      {inReview ? <span className="dmp-muted"> · {inReview} review</span> : null}
                    </dd>
                  </div>
                  <div>
                    <dt>Posts</dt>
                    <dd>{fmtNum(p.Counts?.posts)}</dd>
                  </div>
                  <div>
                    <dt>Campaigns</dt>
                    <dd>{fmtNum(p.Counts?.campaigns)}</dd>
                  </div>
                  <div>
                    <dt>Leads</dt>
                    <dd>{fmtNum(p.Counts?.leads)}</dd>
                  </div>
                  <div>
                    <dt>Spend</dt>
                    <dd>{fmtMoney(p.Counts?.spend)}</dd>
                  </div>
                  <div>
                    <dt>Updated</dt>
                    <dd>{fmtDateTime(p.Updated_On)}</dd>
                  </div>
                </dl>
              </ResultsMobileCard>
            );
          })}
        </ResultsMobileList>

        <div className="dmp-table-wrap co-desktop-table">
          <table className="dmp-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Priority</th>
                <th>Stage</th>
                <th>Location</th>
                <th className="dmp-num">Content</th>
                <th className="dmp-num">Posts</th>
                <th className="dmp-num">Campaigns</th>
                <th className="dmp-num">Leads</th>
                <th className="dmp-num">Spend</th>
                <th>Assigned</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="dmp-empty">
                    Loading…
                  </td>
                </tr>
              ) : projects.length === 0 ? (
                <tr>
                  <td colSpan={11} className="dmp-empty">
                    {canCreate ? "No projects yet. Use “New project” to start." : "No projects yet."}
                  </td>
                </tr>
              ) : (
                projects.map((p) => (
                  <tr key={p.Dm_Project_Id} className="is-clickable" onClick={() => navigate(`${basePath}/${p.Dm_Project_Id}`)}>
                    <td>
                      <strong>{p.Project_Name}</strong>
                      {p.Short_Code ? <div className="dmp-muted">{p.Short_Code}</div> : null}
                    </td>
                    <td>
                      <Badge value={p.Priority} />
                    </td>
                    <td>
                      <Badge value={p.Stage} />
                    </td>
                    <td>{p.Location || <span className="dmp-muted">—</span>}</td>
                    <td className="dmp-num">
                      {fmtNum(p.Counts?.content)}
                      {p.Counts?.pending_review ? <div className="dmp-muted">{p.Counts.pending_review} in review</div> : null}
                    </td>
                    <td className="dmp-num">{fmtNum(p.Counts?.posts)}</td>
                    <td className="dmp-num">{fmtNum(p.Counts?.campaigns)}</td>
                    <td className="dmp-num">{fmtNum(p.Counts?.leads)}</td>
                    <td className="dmp-num">{fmtMoney(p.Counts?.spend)}</td>
                    <td>{p.Assigned_To_Name || <span className="dmp-muted">—</span>}</td>
                    <td className="dmp-muted">{fmtDateTime(p.Updated_On)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default ProjectList;
